import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  LinearProgress
} from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts'
import { Download, Refresh } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import medicalCodingService from '../../../services/medicalCodingService'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

const CodingStats = () => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [statistics, setStatistics] = useState(null)
  const [dateRange, setDateRange] = useState('MONTH')
  const [verificationFilter, setVerificationFilter] = useState('ALL')

  useEffect(() => {
    loadStatistics()
  }, [dateRange, verificationFilter])

  const loadStatistics = async () => {
    setLoading(true)
    try {
      const params = {
        dateRange: dateRange !== 'ALL' ? dateRange : undefined,
        verified: verificationFilter !== 'ALL' ? verificationFilter === 'VERIFIED' : undefined
      }

      const response = await medicalCodingService.getCodingStatistics(params)
      
      if (response.data.success) {
        setStatistics(response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load statistics', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ title, value, subtitle, color = 'primary' }) => (
    <Card>
      <CardContent sx={{ textAlign: 'center' }}>
        <Typography variant="h4" color={color} fontWeight={600}>
          {value}
        </Typography>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  if (loading && !statistics) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Loading Statistics...
        </Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Coding Statistics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="ALL">All Time</MenuItem>
              <MenuItem value="TODAY">Today</MenuItem>
              <MenuItem value="WEEK">This Week</MenuItem>
              <MenuItem value="MONTH">This Month</MenuItem>
              <MenuItem value="QUARTER">This Quarter</MenuItem>
              <MenuItem value="YEAR">This Year</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={verificationFilter}
              label="Status"
              onChange={(e) => setVerificationFilter(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="VERIFIED">Verified</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            startIcon={<Refresh />}
            onClick={loadStatistics}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            startIcon={<Download />}
            variant="outlined"
          >
            Export
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {statistics && (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Coded"
              value={statistics.overall?.totalCoded || 0}
              subtitle="All records"
              color="primary"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Verified"
              value={statistics.overall?.verified || 0}
              subtitle={`${((statistics.overall?.verified / statistics.overall?.totalCoded) * 100 || 0).toFixed(1)}% rate`}
              color="success"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending"
              value={statistics.overall?.pending || 0}
              subtitle="Awaiting verification"
              color="warning"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Rejected"
              value={statistics.overall?.rejected || 0}
              subtitle="Need revision"
              color="error"
            />
          </Grid>

          {/* Coder Performance */}
          {statistics.byCoder && statistics.byCoder.length > 0 && (
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Coder Performance
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statistics.byCoder}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="coderName" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8884d8" name="Total Coded" />
                      <Bar dataKey="verifiedCount" fill="#82ca9d" name="Verified" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* ICD Chapter Distribution */}
          {statistics.byICDChapter && statistics.byICDChapter.length > 0 && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ICD Chapter Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statistics.byICDChapter.slice(0, 5)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name.split('-')[0]} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {statistics.byICDChapter.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Monthly Trends */}
          {statistics.monthlyTrends && statistics.monthlyTrends.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Monthly Trends
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={statistics.monthlyTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="coded" 
                        stroke="#8884d8" 
                        name="Coded"
                        strokeWidth={2}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="verified" 
                        stroke="#82ca9d" 
                        name="Verified"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* DRG Distribution */}
          {statistics.drgDistribution && statistics.drgDistribution.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top DRG Groups
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statistics.drgDistribution.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="drgCode" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FF8042" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Top Procedures */}
          {statistics.topProcedures && statistics.topProcedures.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Most Common Procedures
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statistics.topProcedures.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="_id" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#FFBB28" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  )
}

export default CodingStats